// menu-bar.component.ts

import {
  Component,
  ChangeDetectionStrategy,
  NgZone,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { AsyncPipe, CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { FeedbackService } from '@services/feedback.service';
import { GaService } from '@analytics/ga.service';

@Component({
  selector: 'app-menu-bar',
  standalone: true,
  imports: [AsyncPipe, CommonModule, FormsModule],
  templateUrl: './menu-bar.component.html',
  styleUrl: './menu-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuBarComponent implements OnInit, AfterViewInit, OnDestroy {
  username: string | null = null;

  // Render-safe placeholder during prerender/SSR to avoid hydration mismatches.
  time$: Observable<string> = of('');

  feedbackText = '';
  isFeedbackFocused = false;
  sending = false;
  justSent = false;
  errorMsg: string | null = null;

  private sentTimer?: ReturnType<typeof setTimeout>;
  readonly MAX_FEEDBACK_LEN = 1000;
  private typingStart = 0;

  email = '';

  private readonly isBrowser: boolean;

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private feedbackSvc: FeedbackService,
    private ga: GaService,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  @ViewChild('feedbackBox') feedbackBox!: ElementRef<HTMLTextAreaElement>;

  ngOnInit(): void {
    // Keep time$ empty on the server and during the very first client render.
    // This prevents SSR/prerendered markup from differing from the client hydration pass.
    this.time$ = of('');
  }

  ngAfterViewInit(): void {
    // Start ticking only in the browser *after* hydration has attached.
    if (!this.isBrowser) return;

    this.time$ = new Observable<string>((observer) => {
      const fmt = new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      });

      return this.ngZone.runOutsideAngular(() => {
        const emit = () =>
          this.ngZone.run(() => observer.next(fmt.format(new Date())));

        emit();
        const id = window.setInterval(emit, 1000);

        return () => window.clearInterval(id);
      });
    });

    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.sentTimer) clearTimeout(this.sentTimer);
  }

  onEmailInput(ev: Event) {
    const el = ev.target as HTMLInputElement;
    this.email = el.value;
  }

  onFeedbackInput(ev: Event) {
    const el = ev.target as HTMLTextAreaElement;

    if (el.value.length > this.MAX_FEEDBACK_LEN) {
      el.value = el.value.slice(0, this.MAX_FEEDBACK_LEN);
    }

    this.feedbackText = el.value;

    if (!this.typingStart && this.feedbackText.trim().length) {
      this.typingStart = Date.now();
    }

    this.autoGrow(el);
  }

  async submitFeedback(ev: Event): Promise<void> {
    ev.preventDefault();
    if (!this.isBrowser) return;

    const text = this.feedbackText.trim();
    if (!text || this.sending) return;

    const charCount = text.length;
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    const typingMs = this.typingStart
      ? Date.now() - this.typingStart
      : undefined;

    const path = window.location.pathname;
    const m = /^\/entries\/([^/]+)\/([^/]+)/.exec(path);
    const moduleSlug = m?.[1];
    const entrySlug = m?.[2];

    const emailTrimmed = this.email?.trim();
    const emailRaw = emailTrimmed ? emailTrimmed : undefined;

    const SENT_PILL_MS = 2000;

    this.errorMsg = null;
    this.sending = true;
    this.cdr.markForCheck();

    try {
      await this.feedbackSvc.send(text, {
        charCount,
        wordCount,
        typingMs,
        ...(moduleSlug ? { module: moduleSlug } : {}),
        ...(entrySlug ? { entrySlug } : {}),
        ...(emailRaw ? { emailRaw } : {}),
        path: window.location.pathname,
        url: window.location.href,
        lang: navigator.language,
        tzOffsetMin: new Date().getTimezoneOffset(),
        viewport: {
          w: window.innerWidth,
          h: window.innerHeight,
          dpr: window.devicePixelRatio || 1,
        },
      } as any);

      // Track feedback submit event (browser-only)
      this.ga.event('feedback_submit', {
        char_count: charCount,
        module: moduleSlug ?? '(none)',
        entry_slug: entrySlug ?? '(none)',
      });

      this.feedbackText = '';
      this.justSent = true;
      this.typingStart = 0;
      this.cdr.markForCheck();

      if (this.sentTimer) clearTimeout(this.sentTimer);
      this.sentTimer = setTimeout(() => {
        this.justSent = false;
        this.cdr.markForCheck();
      }, SENT_PILL_MS);
    } catch (err: any) {
      this.errorMsg = 'Failed to send';
      this.cdr.markForCheck();
      console.error(err);
    } finally {
      this.sending = false;
      this.cdr.markForCheck();
    }
  }

  autoGrow(el: HTMLTextAreaElement) {
    if (!this.isBrowser) return;

    const max = Math.round(window.innerHeight * 0.4);
    el.style.height = '0px';
    const h = Math.min(el.scrollHeight, max);
    el.style.height = h + 'px';
    el.style.overflowY = el.scrollHeight > h ? 'auto' : 'hidden';
  }

  onFeedbackFocusIn() {
    this.isFeedbackFocused = true;

    queueMicrotask(() => {
      const el = this.feedbackBox?.nativeElement;
      if (el) this.autoGrow(el);
    });
  }

  onFeedbackFocusOut(ev: FocusEvent) {
    const form = ev.currentTarget as HTMLElement;
    if (!form.contains(ev.relatedTarget as Node)) {
      this.isFeedbackFocused = false;

      const el = this.feedbackBox?.nativeElement;
      if (el) {
        el.style.height = '';
        el.style.overflowY = 'hidden';
      }
    }
  }
}
